import { useCallback, useEffect, useRef, useState } from "react";

import Dropdown from "components/dropdown/Dropdown";
import { Input } from "components/input/Input";
import Paginator from "components/paginator/Paginator";
import { Tooltip } from "components/tooltip/Tooltip";
import { SortingOptions } from "../containers/Documents";
import { useRootStore } from "store";

type DocumentsFiltersProps = {
  sortingOptions: SortingOptions;
};

type DocumentParams = {
  pageSize: number;
  lastEvaluatedKey?: string;
  categoryParam?: string;
  emailParam?: string;
  searchParam?: string;
  dateSortParam?: string;
};

export function DocumentsFilters({ sortingOptions }: DocumentsFiltersProps) {
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const {
    fetchCustomerDocuments,
    setCustomerDocumentsPageNumber,
    resetCustomerDocuments,
    setCustomerDocumentsPageSize
  } = useRootStore((state) => state.customers);

  const {
    data,
    emails,
    categories,
    total,
    pageSize,
    currentPage,
    totalFiltered
  } = useRootStore((state) => state.customers.workingCustomer.documents);
  const { organizationNumber } = useRootStore(
    (state) => state.customers.workingCustomer
  );

  const [filters, setFilters] = useState({
    category: "all",
    from: "all",
    searchCriteria: ""
  });

  // fetching the first item from the next page
  // to check if lastEvaluatedKey is null
  // meaning we have reached the last page (next page will be last page)
  const [documentsParams, setDocumentsParams] = useState<DocumentParams>({
    pageSize: pageSize + 1
  });

  const prevFiltersRef = useRef(filters);
  const prevSortingOptionsRef = useRef(sortingOptions);
  const prevPageRef = useRef<number | null>(null);

  const handleFilterChange = () => {
    const baseParams = {
      pageSize: pageSize + 1
    };

    const params = {
      ...baseParams,
      ...(filters.searchCriteria && { searchCriteria: filters.searchCriteria }),
      ...(filters.category !== "all" && { categorySearch: filters.category }),
      ...(filters.from !== "all" && { emailSearch: filters.from }),
      ...(sortingOptions.sortByDateOrder && {
        dateSort: sortingOptions.sortByDateOrder
      })
    };

    const haveFiltersChanged =
      prevFiltersRef.current.searchCriteria !== filters.searchCriteria ||
      prevFiltersRef.current.category !== filters.category ||
      prevFiltersRef.current.from !== filters.from ||
      prevSortingOptionsRef.current.sortByDateOrder !==
        sortingOptions.sortByDateOrder;

    if (haveFiltersChanged) {
      setCustomerDocumentsPageNumber(1);
      resetCustomerDocuments();
      params.pageSize = pageSize + 1;

      if (organizationNumber) {
        fetchCustomerDocuments(organizationNumber, params);
      }
    }

    setDocumentsParams(params);
  };

  useEffect(() => {
    handleFilterChange();

    // Update the previous filter and sorting reference
    prevFiltersRef.current = filters;
    prevSortingOptionsRef.current = sortingOptions;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, pageSize, sortingOptions]);

  const all = { label: "Allt", value: "all" };

  const categoryOptions = categories
    ? Object.keys(categories).map((key) => {
        const categoryLabel =
          key.toLowerCase() === "unknown" ? "Okänd" : decodeURI(key);
        const option = {
          label: `${categoryLabel}(${categories[key]})`,
          value: key
        };
        return option;
      })
    : [];
  categoryOptions.unshift(all);

  const fromOptions = emails
    ? Object.keys(emails).map((key) => {
        const option = { label: `${key}(${emails[key]})`, value: key };
        return option;
      })
    : [];
  fromOptions.unshift(all);

  const handleOnFilterChange = (
    value: string,
    filterType: "category" | "from"
  ) => {
    const updatedFilters =
      filterType === "from"
        ? { ...filters, from: value }
        : { ...filters, category: value };

    setFilters(updatedFilters);
  };

  const handleOnSearch = useCallback((value: string) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setFilters((prevState) => ({ ...prevState, searchCriteria: value }));
    }, 800);
  }, []);

  const handleNextPage = (chosenPage: number) => {
    const newPageStartIndex = (chosenPage - 1) * pageSize;
    const newPageEndIndex = newPageStartIndex + pageSize;
    if (data && organizationNumber && data.lastEvaluatedKey) {
      const itemsInStore = data.skickaDocuments.slice(
        newPageStartIndex,
        newPageEndIndex
      );
      if (itemsInStore.length < pageSize) {
        const params = {
          ...documentsParams,
          lastEvaluatedKey: data.lastEvaluatedKey
        };
       fetchCustomerDocuments(organizationNumber, params);
        setDocumentsParams(params);
      }
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (data) {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + newPageSize;
      const documentsOnCurrentPage = data.skickaDocuments.slice(
        startIndex,
        endIndex
      );
      // recalculate and set current page on page size change
      const recalculatedCurrentPage = Math.ceil((startIndex + 1) / newPageSize);

      setCustomerDocumentsPageNumber(recalculatedCurrentPage);

      if (organizationNumber && documentsParams && data.lastEvaluatedKey) {
        // if there are not enough documents to show, fetch more
        if (documentsOnCurrentPage.length < newPageSize) {
          const params = {
            ...documentsParams,
            pageSize: newPageSize + 1,
            lastEvaluatedKey: data.lastEvaluatedKey
          };
          fetchCustomerDocuments(organizationNumber, params);
          setDocumentsParams(params);
        }
      }
    }
  };

  return (
    <div className="documents-filters d-flex justify-content-between align-items-center flex-wrap">
      <div className="documents-filters-wrap d-flex align-items-center flex-wrap">
        <div className="documents-filters-item d-flex align-items-center mr-3 flex-wrap mb-4">
          <span className="text-gray">Skickat från:</span>
          <Tooltip
            className="mr-3"
            content={
              <p className="m-0">
                {
                  fromOptions.find((option) => option.value === filters.from)
                    ?.label
                }
              </p>
            }
            enabled={filters.from !== "all"}
          >
            <Dropdown
              isClearable={false}
              options={fromOptions}
              fixedWidth={false}
              className="documents-filter-dropdown from"
              value={filters.from}
              onChange={(selected) => {
                if (selected && !Array.isArray(selected)) {
                  const selectedOption = selected as {
                    value: string;
                    label: string;
                  };
                  handleOnFilterChange(selectedOption.value, "from");
                }
              }}
            />
          </Tooltip>
        </div>
        <div className="documents-filters-item d-flex align-items-center flex-wrap mr-3 mb-4">
          <span className="text-gray">Kategori:</span>
          <Tooltip
            className="mr-3"
            content={
              <p className="m-0">
                {
                  categoryOptions.find(
                    (option) => option.value === filters.category
                  )?.label
                }
              </p>
            }
            enabled={filters.category !== "all"}
          >
            <Dropdown
              isClearable={false}
              options={categoryOptions}
              className="documents-filter-dropdown category"
              fixedWidth={false}
              value={filters.category}
              onChange={(selected) => {
                if (selected && !Array.isArray(selected)) {
                  const selectedOption = selected as {
                    value: string;
                    label: string;
                  };
                  handleOnFilterChange(selectedOption.value, "category");
                }
              }}
            />
          </Tooltip>
        </div>
        <div className="vertical-separator mb-4"></div>
        <div className="documents-filters-item ml-4 mb-4">
          <Input
            variant="rounded"
            size="slim"
            className="documents-filter-search"
            placeholder="Börja skriva här..."
            hasSearchIcon
            onChange={(e) => handleOnSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="mb-4">
        <Paginator
          itemsLength={totalFiltered || total || 0}
          defaultItemsShownPerPage={pageSize}
          showLastPage={data?.lastEvaluatedKey === null}
          onSelectPage={(value) => {
            prevPageRef.current = currentPage;
           setCustomerDocumentsPageNumber(value);
            handleNextPage(value);
          }}
          onChangeShownItems={(value) => {
            handlePageSizeChange(value);
           setCustomerDocumentsPageSize(value);
          }}
          presetActivePage={currentPage}
        />
      </div>
    </div>
  );
}
